import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Clock,
    Users,
    CheckCircle,
    XCircle,
    RefreshCw,
    Filter,
    ChevronLeft,
    ChevronRight,
    UserCheck,
    Calendar,
    Search,
} from "lucide-react";
import "./AgendaManager.css";
import { API_BASE_URL } from "../../../../api";

import { fetchParamCongesRequest } from "../../../redux/actions/paramCongesActions";
import { getDirectReportsRequest } from "../../../redux/actions/userActions";
import { getManagerTeamLeavesRequest } from "../../../redux/actions/leaveRequestsActions";
import {
    getCalendarSettingsRequest,
    getWorkdaysConfigRequest,
} from "../../../redux/actions/workDaysActions";


const DEMO_MODE = false; // 👉 mets false pour repasser en mode normal
const weekDays = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const statusLabel = (status, t) => t({ validé: "Validée", en_attente: "En attente", refusé: "Refusée", annulé: "Annulée", reporté: "Reportée" }[status] || status, { defaultValue: status });

const mockTypesAbsences = [
    { id: 1, nom: "Congé annuel", code: "CA", couleur: "#3b82f6" },
    { id: 2, nom: "Congé maladie", code: "CM", couleur: "#ef4444" },
    { id: 3, nom: "Congé sans solde", code: "CSS", couleur: "#f59e0b" },
];

const mockDirectReports = [
    { id: 1, firstName: "Sara", lastName: "El Fassi", profilePictureUrl: "https://i.pravatar.cc/150?img=5" },
    { id: 2, firstName: "Yassine", lastName: "Amrani", profilePictureUrl: "https://i.pravatar.cc/150?img=12" },
    { id: 3, firstName: "Nadia", lastName: "Rahmouni", profilePictureUrl: "https://i.pravatar.cc/150?img=47" },
    { id: 4, firstName: "Omar", lastName: "Idrissi", profilePictureUrl: "https://i.pravatar.cc/150?img=33" },
];

const mockTeamLeaves = [
    { id: 1, employeeId: 1, employeeName: "Sara El Fassi", absenceTypeNom: "Congé annuel", dateDebut: "2026-08-05", dateFin: "2026-08-09", status: "APPROVED", commentaire: "Vacances d'été", duree: 5 },
    { id: 2, employeeId: 2, employeeName: "Yassine Amrani", absenceTypeNom: "Congé maladie", dateDebut: "2026-08-11", dateFin: "2026-08-12", status: "APPROVED", commentaire: "Grippe", duree: 2 },
    { id: 3, employeeId: 3, employeeName: "Nadia Rahmouni", absenceTypeNom: "Congé annuel", dateDebut: "2026-08-17", dateFin: "2026-08-21", status: "PENDING", commentaire: "Voyage", duree: 5 },
    { id: 4, employeeId: 4, employeeName: "Omar Idrissi", absenceTypeNom: "Congé sans solde", dateDebut: "2026-08-24", dateFin: "2026-08-25", status: "CANCELLED", commentaire: "Raison personnelle", duree: 2 },
    { id: 5, employeeId: 1, employeeName: "Sara El Fassi", absenceTypeNom: "Congé annuel", dateDebut: "2026-08-27", dateFin: "2026-08-28", status: "POSTPONED", commentaire: "Reporté", duree: 2 },
];


const ProfileAvatar = ({ collaborator, className = "" }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const picture = String(collaborator?.profilePictureUrl || "").trim();
    const imageUrl = picture
        ? (picture.startsWith("http") ? picture : `${API_BASE_URL}/api/users/images/profile/${picture}`)
        : null;
    const initials = collaborator?.avatar || collaborator?.nom?.split(" ").map((name) => name[0]).join("") || "?";

    return (
        <span className={`amProfileAvatar ${className}`} aria-hidden="true">
            {imageUrl && !imageFailed
                ? <img src={imageUrl} alt="" onError={() => setImageFailed(true)} />
                : <span>{initials.slice(0, 2).toUpperCase()}</span>}
        </span>
    );
};

const AgendaManager = () => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === "ar";
    const locale = isRtl ? "ar-MA" : i18n.language === "en" ? "en-US" : "fr-FR";
    const dispatch = useDispatch();
    const uid = sessionStorage.getItem("uid");

    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [startDateFilter, setStartDateFilter] = useState("");
    const [endDateFilter, setEndDateFilter] = useState("");
    const [selectedCollaborator, setSelectedCollaborator] = useState("all");
    const [selectedType, setSelectedType] = useState("all");
    const [collaboratorSearch, setCollaboratorSearch] = useState("");
    const [isCollaboratorsOpen, setIsCollaboratorsOpen] = useState(true);

    // === REDUX STATE ===
    const { typesAbsences: reduxTypesAbsences } = useSelector((state) => state.paramConges);
    const { directReports: reduxDirectReports, directReportsLoading } = useSelector((state) => state.user);
    const { teamLeaves: reduxTeamLeaves, loading: leavesLoading } = useSelector((state) => state.leaveRequests);
    const { workdaysConfig, calendarSettings } = useSelector((state) => state.workDays || {});

    const typesAbsences = DEMO_MODE ? mockTypesAbsences : reduxTypesAbsences;
    const directReports = DEMO_MODE ? mockDirectReports : reduxDirectReports;
    const teamLeaves = DEMO_MODE ? mockTeamLeaves : reduxTeamLeaves;

    useEffect(() => {
        if (!DEMO_MODE) {
            dispatch(fetchParamCongesRequest());
            if (uid) {
                dispatch(getDirectReportsRequest(uid));
            }
            dispatch(getManagerTeamLeavesRequest());
            dispatch(getWorkdaysConfigRequest());
        }
    }, [dispatch, uid]);

    useEffect(() => {
        if (!DEMO_MODE) {
            dispatch(getCalendarSettingsRequest(currentMonth.getFullYear(), "MA"));
        }
    }, [currentMonth, dispatch]);

    const fixedHolidays = useMemo(() => (calendarSettings?.fixed || []).map((holiday) => ({
        date: holiday.key,
        description: holiday.label,
        selected: !!holiday.selected,
    })), [calendarSettings]);

    const hijriHolidays = useMemo(() => (calendarSettings?.hijri || []).map((holiday) => ({
        name: holiday.name,
        date: holiday.date || "",
        duration: Number(holiday.duration || 1),
        selected: !!holiday.selected,
    })), [calendarSettings]);

    const customHolidays = useMemo(() => calendarSettings?.custom || [], [calendarSettings]);

    // === MAPPING DATA ===
    const collaborateurs = useMemo(() => {
        const isActif = (u) => u && u.enabled !== false && u.actif !== false && u.isActif !== false && u.statut !== "INACTIF";
        return (directReports || [])
            .filter(isActif)
            .map(u => ({
                id: u.id,
                nom: `${u.firstName} ${u.lastName}`,
                avatar: `${u.firstName?.charAt(0) || ""}${u.lastName?.charAt(0) || ""}`,
                profilePictureUrl: u.profilePictureUrl,
            }));
    }, [directReports]);

    const collaborateursParId = useMemo(
        () => new Map(collaborateurs.map((collaborateur) => [String(collaborateur.id), collaborateur])),
        [collaborateurs]
    );

    const filteredCollaborateurs = useMemo(() => {
        const query = collaboratorSearch.trim().toLocaleLowerCase(locale);
        if (!query) return collaborateurs;
        return collaborateurs.filter((collaborateur) => collaborateur.nom.toLocaleLowerCase(locale).includes(query));
    }, [collaborateurs, collaboratorSearch, locale]);

    const conges = useMemo(() => {
        return (teamLeaves || []).map(l => {
            // Mapping status
            let statut = "en_attente";
            if (l.status === "APPROVED") statut = "validé";
            if (l.status === "REJECTED") statut = "refusé";
            if (l.status === "CANCELLED") statut = "annulé";
            if (l.status === "POSTPONED") statut = "reporté";

            return {
                id: l.id,
                collaborateurId: l.employeeId,
                collaborateurNom: l.employeeName,
                type: l.absenceTypeNom,
                debut: l.dateDebut,
                fin: l.dateFin,
                statut: statut,
                motif: l.commentaire,
                duree: l.duree
            };
        });
    }, [teamLeaves]);

    const getPublicHolidayName = (date) => {
        if (!date) return "";
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const fullDate = `${date.getFullYear()}-${month}-${day}`;
        const names = [];

        const fixedHoliday = fixedHolidays.find((holiday) => holiday.date === `${month}-${day}` && holiday.selected);
        if (fixedHoliday) names.push(t(fixedHoliday.description, { defaultValue: fixedHoliday.description }));

        hijriHolidays.forEach((holiday) => {
            if (!holiday.selected || !holiday.date) return;
            const start = new Date(holiday.date);
            start.setHours(0, 0, 0, 0);
            for (let i = 0; i < (holiday.duration || 1); i++) {
                const occurrence = new Date(start);
                occurrence.setDate(occurrence.getDate() + i);
                const occurrenceDate = `${occurrence.getFullYear()}-${String(occurrence.getMonth() + 1).padStart(2, "0")}-${String(occurrence.getDate()).padStart(2, "0")}`;
                if (occurrenceDate === fullDate) {
                    const holidayName = t(holiday.name, { defaultValue: holiday.name });
                    names.push(holiday.duration > 1 ? `${holidayName} (${i + 1}/${holiday.duration})` : holidayName);
                    break;
                }
            }
        });

        const customHoliday = customHolidays.find((holiday) => holiday.date === fullDate);
        if (customHoliday) names.push(t(customHoliday.description, { defaultValue: customHoliday.description }));
        return names.join(" • ");
    };

    const isNonWorkingDay = (date) => {
        if (!date) return false;
        const weekdays = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
        const dayName = weekdays[date.getDay() === 0 ? 6 : date.getDay() - 1];
        const config = workdaysConfig?.workDays?.[dayName];
        const isWorking = typeof config?.isWorking === "boolean"
            ? config.isWorking
            : typeof config?.working === "boolean"
                ? config.working
                : !(dayName === "samedi" || dayName === "dimanche");
        return !isWorking;
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];
        const startOffset = firstDay === 0 ? 6 : firstDay - 1;

        for (let i = 0; i < startOffset; i++) days.push(null);
        for (let day = 1; day <= daysInMonth; day++) days.push(new Date(year, month, day));
        // Six semaines fixes pour conserver une grille compacte et stable.
        while (days.length < 42) days.push(null);
        return days;
    };

    const days = useMemo(() => getDaysInMonth(currentMonth), [currentMonth]);

    const filteredConges = useMemo(() => {
        return conges.filter((c) => {
            if (selectedCollaborator !== "all" && String(c.collaborateurId) !== selectedCollaborator) return false;
            if (selectedType !== "all" && c.type !== selectedType) return false;
            // Un congé est retenu si sa période chevauche celle recherchée.
            if (startDateFilter && c.fin < startDateFilter) return false;
            if (endDateFilter && c.debut > endDateFilter) return false;
            return true;
        });
    }, [conges, selectedCollaborator, selectedType, startDateFilter, endDateFilter]);

    const getCongesForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split("T")[0];
        return filteredConges.filter((c) => c.debut <= dateStr && c.fin >= dateStr);
    };

    const getTypeColor = (typeNom) => {
        const type = (typesAbsences || []).find((t) => t.nom === typeNom);
        return type?.couleur || "#6b7280";
    };

    const changeMonth = (increment) => {
        setCurrentMonth((prev) => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + increment);
            return newDate;
        });
    };

    const selectStartDate = (value) => {
        setStartDateFilter(value);
        if (!value) return;
        const [year, month] = value.split("-").map(Number);
        setCurrentMonth(new Date(year, month - 1, 1));
    };

    const monthName = currentMonth.toLocaleString(locale, { month: "long", year: "numeric" });

    return (
        <div className="amPage">
            <header className="amHeader">
                <div>
                    <h1>{t("Agenda des congés")}</h1>
                    {/*<p>Suivez les absences et validez les demandes de vos collaborateurs</p>*/}
                </div>
                {(directReportsLoading || leavesLoading) && <div className="amLoader">{t("Chargement...")}</div>}
            </header>

            <div className="amCard">
                <div className="amBody">
                    {/* Filtres */}
                    <div className="amFilters">
                        <div className="amFilters__label">
                            <Filter size={16} />
                            <span>{t("Filtres :")}</span>
                        </div>

                        <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="amSelect">
                            <option value="all">{t("Tous les types")}</option>
                            {(typesAbsences || []).map((type) => (
                                <option key={type.id} value={type.nom}>
                                    {t(type.nom, { defaultValue: type.nom })}
                                </option>
                            ))}
                        </select>

                    </div>

                    <div className={`amContentLayout ${isCollaboratorsOpen ? "" : "is-collapsed"}`}>
                        <aside className={`amCollaborators ${isCollaboratorsOpen ? "" : "is-collapsed"}`} aria-label={t("Filtrer par collaborateur")}>
                            <div className="amCollaborators__title"><span>{t("Collaborateurs")}</span><button type="button" className="amCollaborators__toggle" onClick={() => setIsCollaboratorsOpen((open) => !open)} title={t(isCollaboratorsOpen ? "Réduire le panneau collaborateurs" : "Développer le panneau collaborateurs")} aria-label={t(isCollaboratorsOpen ? "Réduire le panneau collaborateurs" : "Développer le panneau collaborateurs")}>{isCollaboratorsOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}</button></div>
                            {isCollaboratorsOpen && <>
                            <div className="amCollaboratorSearch">
                                <Search size={14} />
                                <input type="search" value={collaboratorSearch} onChange={(event) => setCollaboratorSearch(event.target.value)} placeholder={t("Rechercher un collaborateur...")} aria-label={t("Rechercher un collaborateur")} />
                            </div>
                            <button
                                type="button"
                                className={`amCollaborator ${selectedCollaborator === "all" ? "is-selected" : ""}`}
                                onClick={() => setSelectedCollaborator("all")}
                            >
                                <span className="amAllAvatar"><Users size={15} /></span>
                                <span>{t("Tous les collaborateurs")}</span>
                            </button>
                            <div className="amCollaborators__list">
                                {filteredCollaborateurs.map((collaborateur) => (
                                    <button
                                        key={collaborateur.id}
                                        type="button"
                                        className={`amCollaborator ${selectedCollaborator === String(collaborateur.id) ? "is-selected" : ""}`}
                                        onClick={() => setSelectedCollaborator(String(collaborateur.id))}
                                        title={collaborateur.nom}
                                    >
                                        <ProfileAvatar collaborator={collaborateur} />
                                        <span>{collaborateur.nom}</span>
                                    </button>
                                ))}
                                {filteredCollaborateurs.length === 0 && <div className="amCollaborators__empty">{t("Aucun collaborateur trouvé")}</div>}
                            </div>
                            </>}
                        </aside>

                        <main className="amAgenda">
                            {/* Navigation mois */}
                            <div className="amMonthBar">
                                <div className="amMonthHeading">
                                    <h2 className="amMonthTitle">
                                        {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
                                    </h2>
                                    <label className="amDateSearch">
                                        <span>{t("Du")}</span>
                                        <input type="date" value={startDateFilter} onChange={(event) => selectStartDate(event.target.value)} aria-label={t("Date de début du congé")} />
                                        <span>{t("Au")}</span>
                                        <input type="date" value={endDateFilter} min={startDateFilter || undefined} onChange={(event) => setEndDateFilter(event.target.value)} aria-label={t("Date de fin du congé")} />
                                    </label>
                                </div>

                                <div className="amMonthActions">
                                    <button type="button" className="amIconCircle" onClick={() => changeMonth(-1)} title={t("Mois précédent")}>
                                        <ChevronLeft size={20} />
                                    </button>

                                    <button type="button" className="amTodayBtn" onClick={() => setCurrentMonth(new Date())}>
                                        {t("Aujourd'hui")}
                                    </button>

                                    <button type="button" className="amIconCircle" onClick={() => changeMonth(1)} title={t("Mois suivant")}>
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Grille calendrier */}
                            <div className="amCalendar">
                                <div className="amWeekHeader">
                                    {weekDays.map((day) => (
                                        <div key={day} className="amWeekDay">
                                            {t(day)}
                                        </div>
                                    ))}
                                </div>

                                <div className="amGrid">
                                    {days.map((date, idx) => {
                                        const congesDuJour = date ? getCongesForDate(date) : [];
                                        const isToday = date && date.toDateString() === new Date().toDateString();
                                        const dayNumber = date ? date.getDate() : "";
                                        const publicHolidayName = date ? getPublicHolidayName(date) : "";
                                        const nonWorkingDay = date && isNonWorkingDay(date);

                                        return (
                                            <div
                                                key={idx}
                                                className={`amCell ${!date ? "is-empty" : ""} ${isToday ? "is-today" : ""} ${publicHolidayName ? "is-holiday" : ""} ${nonWorkingDay && !publicHolidayName ? "is-non-working" : ""}`}
                                                title={publicHolidayName || (nonWorkingDay ? t("Jour non ouvré") : "")}
                                            >
                                                {date && (
                                                    <>
                                                        <div className={`amDayNum ${isToday ? "is-today" : ""}`}>{dayNumber}</div>

                                                        <div className="amCellList">
                                                            {publicHolidayName && (
                                                                <div className="amCalendarMarker is-holiday" title={publicHolidayName}>
                                                                    <Calendar size={12} />
                                                                    <span>{publicHolidayName}</span>
                                                                </div>
                                                            )}
                                                            {nonWorkingDay && !publicHolidayName && (
                                                                <div className="amCalendarMarker is-non-working" title={t("Jour non ouvré")}>
                                                                    <Clock size={12} />
                                                                    <span>{t("Non ouvré")}</span>
                                                                </div>
                                                            )}
                                                            {congesDuJour.slice(0, 2).map((conge, i) => {
                                                                const type = (typesAbsences || []).find((t) => t.nom === conge.type);
                                                                const displayName = conge.collaborateurNom || "";
                                                                const collaborator = collaborateursParId.get(String(conge.collaborateurId)) || {
                                                                    nom: displayName,
                                                                    avatar: displayName.split(" ").map((name) => name[0]).join("")
                                                                };

                                                                return (
                                                                    <div
                                                                        key={`${conge.id}-${i}`}
                                                                        className={`amChip ${conge.statut === 'annulé' ? 'is-cancelled' : ''} ${conge.statut === 'reporté' ? 'is-postponed' : ''}`}
                                                                        style={{ backgroundColor: conge.statut === 'validé' || conge.statut === 'en_attente' ? getTypeColor(conge.type) : undefined }}
                                                                        title={`${t(type?.nom || conge.type, { defaultValue: type?.nom || conge.type })} — ${displayName} (${statusLabel(conge.statut, t)})`}
                                                                    >
                                                                        {conge.statut === 'annulé' ? <XCircle size={12} /> : 
                                                                         conge.statut === 'reporté' ? <RefreshCw size={12} /> : 
                                                                         <UserCheck size={12} />}
                                                                        <ProfileAvatar collaborator={collaborator} className="amChip__avatar" />
                                                                        <span className="amChip__code">{type?.code || "???"}</span>
                                                                        <span className="amChip__sep">—</span>
                                                                        <span className="amChip__name">{displayName}</span>
                                                                    </div>
                                                                );
                                                            })}

                                                            {congesDuJour.length > 2 && (
                                                                <div className="amMore">
                                                                    {t("+{{count}} autre(s)", { count: congesDuJour.length - 2 })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Légende */}
                            <div className="amLegend">
                                <span className="amLegend__title">{t("Légende :")}</span>

                                <div className="amLegend__items">
                                    {(typesAbsences || []).map((type) => (
                                        <div key={type.id} className="amLegendItem">
                                            <div className="amLegendDot" style={{ backgroundColor: type.couleur }} />
                                            <span>{t(type.nom, { defaultValue: type.nom })}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="amLegendHint" style={{ display: 'flex', gap: '15px' }}>
                                    <div className="amLegendItem">
                                        <div className="amLegendDot" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db' }} />
                                        <span style={{ textDecoration: 'line-through', color: '#6b7280' }}>{t("Annulée")}</span>
                                    </div>
                                    <div className="amLegendItem">
                                        <div className="amLegendDot" style={{ backgroundColor: '#e0f2fe', border: '1px solid #bae6fd' }} />
                                        <span style={{ color: '#0369a1' }}>{t("Reportée")}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Users size={16} />
                                        <span>{t("Nom complet affiché")}</span>
                                    </div>
                                    <div className="amLegendItem">
                                        <div className="amLegendDot amLegendDot--holiday" />
                                        <span>{t("Jour férié")}</span>
                                    </div>
                                    <div className="amLegendItem">
                                        <div className="amLegendDot amLegendDot--non-working" />
                                        <span>{t("Jour non ouvré")}</span>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>

                    {/* === LISTE === */}
                    {false && (
                        <div className="amTableWrap">
                            <table className="amTable">
                                <thead>
                                <tr>
                                    <th>{t("Collaborateur")}</th>
                                    <th>{t("Type")}</th>
                                    <th>{t("Période")}</th>
                                    <th>{t("Durée")}</th>
                                    <th>{t("Motif")}</th>
                                    <th>{t("Statut")}</th>
                                </tr>
                                </thead>

                                <tbody>
                                {filteredConges.map((conge) => {
                                    const type = (typesAbsences || []).find((t) => t.nom === conge.type);

                                    return (
                                        <tr key={conge.id}>
                                            <td>
                                                <div className="amCollab">
                                                    <div className="amAvatar">
                                                        {conge.collaborateurNom?.split(" ").map(n => n[0]).join("").toUpperCase().substring(0, 2)}
                                                    </div>
                                                    <span className="amCollabName">{conge.collaborateurNom}</span>
                                                </div>
                                            </td>

                                            <td>
                          <span className="amTypeCode" style={{ backgroundColor: getTypeColor(conge.type) }}>
                            {type?.code}
                          </span>
                                            </td>

                                            <td>
                                                {new Date(conge.debut).toLocaleDateString(locale)} → {new Date(conge.fin).toLocaleDateString(locale)}
                                            </td>

                                            <td className="amStrong">
                                                {conge.duree} {t("jour(s)")}
                                            </td>

                                            <td className="amMuted">{conge.motif ? t(conge.motif, { defaultValue: conge.motif }) : "-"}</td>

                                            <td>
                                                <div className="amStatus">
                                                    {conge.statut === "validé" && <CheckCircle size={16} className="amOk" />}
                                                    {conge.statut === "en_attente" && <Clock size={16} className="amWait" />}
                                                    {conge.statut === "refusé" && <XCircle size={16} className="amNo" />}
                                                    {conge.statut === "annulé" && <XCircle size={16} className="amCancel" />}
                                                    {conge.statut === "reporté" && <RefreshCw size={16} className="amPostpone" />}
                                                    <span className="amStatusTxt">{statusLabel(conge.statut, t)}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>

                            {filteredConges.length === 0 && <div className="amEmpty">{t("Aucune absence trouvée pour les filtres sélectionnés.")}</div>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AgendaManager;
